import mapboxgl from 'mapbox-gl';
import type { MapStyleKey } from './MapboxMap';

// ---------------------------------------------------------------------------
// The searched-location label for the Suburbs ("text based") map style.
//
// The style labels OSM places at a fixed size; the place the customer
// actually searched for gets no emphasis and can be missing at the current
// zoom. This module adds a dedicated overlay label at the searched point and
// suppresses the style's own duplicate of it.
//
// Tuned in frontend/public/label-test/ (the sandbox). SEARCHED_LABEL_STYLE is
// the single source of truth for the look — change it there, not inline.
//
// Only ever applied to the Suburbs style. Streets keeps its marker pin.
// ---------------------------------------------------------------------------

export const SEARCHED_LABEL_SOURCE = 'searched-label';
export const SEARCHED_LABEL_LAYER = 'searched-label';

/** The label's look. `size` is CSS px (Mapbox text-size), independent of the
 *  render container — an offscreen 1024px render and a 7912px print get the
 *  same glyph size scaled by their pixel ratio. */
export const SEARCHED_LABEL_STYLE = {
  font: ['Raleway Black', 'Arial Unicode MS Regular'] as [string, string],
  size: 80,
  letterSpacing: 0.05,
  offsetEm: 0,
  color: '#404342',
  haloColor: '#f4f1ec',
  haloWidth: 1.5,
  uppercase: true,
  // "Always visible" is deliberately OFF — the label participates in the
  // collision system. Centred on its own point it still wins (drawn on top,
  // sort key 0); it only yields in rare crowded pans.
  allowOverlap: false,
  ignorePlacement: false,
};

/** Every style layer whose text could duplicate the searched name. The
 *  searched place might be a suburb, town, city, bay or landform, and each is
 *  a different layer in the Suburbs style — suppression has to cover them all. */
export const SEARCHED_LABEL_SUPPRESS_LAYERS = [
  'settlement-subdivision-label', // neighbourhoods / suburbs
  'settlement-minor-label', // towns, smaller cities
  'settlement-major-label', // major cities
  'natural-point-label', // landforms, wetlands
  'water-point-label', // bays, seas, reservoirs
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Expr = any;

const NAME_EXPR: Expr = ['downcase', ['coalesce', ['get', 'name_en'], ['get', 'name']]];

/** Match the style's own label for suppression: name is exactly the search
 *  term, OR begins with the search term + a space. The prefix case catches
 *  dual names ("Aoraki / Mount Cook" from a search of "Aoraki") without
 *  catching "Yorkshire" from "York". */
export function searchedLabelSuppressExpression(name: string): Expr {
  const nm = name.toLowerCase().trim();
  return [
    'any',
    ['==', NAME_EXPR, nm],
    ['==', ['slice', NAME_EXPR, 0, nm.length + 1], nm + ' '],
  ];
}

// Original filters of the suppressed layers, stashed per map instance so
// removeSearchedLabel() can put them back exactly.
const baseFilters = new WeakMap<mapboxgl.Map, Record<string, unknown>>();

function labelFeature(name: string, coords: [number, number]) {
  return {
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: coords },
    properties: { label: name },
  };
}

/** Add or update the searched-location label on a map. Idempotent — safe to
 *  call on every search and after a setStyle(). No-op until the style is
 *  loaded. Applies to any map instance (the live map or an offscreen render
 *  map); the caller decides whether the current style warrants it. */
export function applySearchedLabel(
  map: mapboxgl.Map,
  place: { name: string; coords: [number, number] },
): void {
  if (!map.isStyleLoaded()) return;
  const cfg = SEARCHED_LABEL_STYLE;

  const existing = map.getSource(SEARCHED_LABEL_SOURCE) as mapboxgl.GeoJSONSource | undefined;
  if (existing) {
    existing.setData(labelFeature(place.name, place.coords) as GeoJSON.Feature);
  } else {
    map.addSource(SEARCHED_LABEL_SOURCE, {
      type: 'geojson',
      data: labelFeature(place.name, place.coords) as GeoJSON.Feature,
    });
  }

  if (!map.getLayer(SEARCHED_LABEL_LAYER)) {
    map.addLayer({
      id: SEARCHED_LABEL_LAYER,
      type: 'symbol',
      source: SEARCHED_LABEL_SOURCE,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': cfg.font,
        'text-size': cfg.size,
        'text-letter-spacing': cfg.letterSpacing,
        'text-offset': [0, cfg.offsetEm],
        'text-transform': cfg.uppercase ? 'uppercase' : 'none',
        'text-allow-overlap': cfg.allowOverlap,
        'text-ignore-placement': cfg.ignorePlacement,
        'symbol-sort-key': 0,
      },
      paint: {
        'text-color': cfg.color,
        'text-halo-color': cfg.haloColor,
        'text-halo-width': cfg.haloWidth,
      },
    } as mapboxgl.LayerSpecification);
  }

  let store = baseFilters.get(map);
  if (!store) {
    store = {};
    baseFilters.set(map, store);
  }
  const expr = searchedLabelSuppressExpression(place.name);
  for (const id of SEARCHED_LABEL_SUPPRESS_LAYERS) {
    if (!map.getLayer(id)) continue;
    if (!(id in store)) store[id] = map.getFilter(id) ?? null;
    const original = store[id];
    map.setFilter(
      id,
      (original ? ['all', original, ['!', expr]] : ['all', ['!', expr]]) as Expr,
    );
  }
}

/** Remove the searched-location label and restore the style's own labels.
 *  Call when switching back to Streets, or to clear the label entirely. */
export function removeSearchedLabel(map: mapboxgl.Map): void {
  if (map.getLayer(SEARCHED_LABEL_LAYER)) map.removeLayer(SEARCHED_LABEL_LAYER);
  if (map.getSource(SEARCHED_LABEL_SOURCE)) map.removeSource(SEARCHED_LABEL_SOURCE);

  const store = baseFilters.get(map);
  if (!store) return;
  for (const id of SEARCHED_LABEL_SUPPRESS_LAYERS) {
    if (id in store && map.getLayer(id)) {
      map.setFilter(id, (store[id] ?? null) as Expr);
    }
  }
  baseFilters.delete(map);
}

/** Whether the searched-location label should be shown for a given style. */
export function styleUsesSearchedLabel(styleKey: MapStyleKey): boolean {
  return styleKey === 'suburbs';
}
